<template>
  <div class="ai-summary-sections">
    <div class="mb-2">
      <p class="body3" v-html="$t('aiAssistant.summary.dateRange')" />
    </div>

    <!-- Summary Section -->
    <div v-if="content.summary" class="mb-6">
      <div class="body2 font-weight-bold mb-3">
        {{ $t("aiAssistant.summary.sections.summary") }}
      </div>
      <ul class="pl-6">
        <li v-for="(item, idx) in content.summary" :key="idx">
          <span class="ai-summary-item-text" v-html="parseMarkdown(item)"></span>
          <a
            href="#"
            class="coro-link text-decoration-underline"
            @click.prevent="onLearnMore(item)"
          >
            {{ $t("aiAssistant.summary.learnMore") }}
          </a>
        </li>
      </ul>
    </div>

    <!-- Trends Section -->
    <div v-if="content.trends" class="mb-6">
      <div class="body2 font-weight-bold mb-3">
        {{ $t("aiAssistant.summary.sections.trends") }}
      </div>
      <ul class="pl-6">
        <li v-for="(item, idx) in content.trends" :key="idx">
          <span class="ai-summary-item-text" v-html="parseMarkdown(item)"></span>
          <a
            href="#"
            class="coro-link text-decoration-underline"
            @click.prevent="onLearnMore(item)"
          >
            {{ $t("aiAssistant.summary.learnMore") }}
          </a>
        </li>
      </ul>
    </div>

    <!-- Recommendations Section -->
    <div v-if="content.recommendations" class="mb-6">
      <div class="body2 font-weight-bold mb-3">
        {{ $t("aiAssistant.summary.sections.recommendations") }}
      </div>
      <ol class="pl-6">
        <li v-for="(item, idx) in content.recommendations" :key="idx">
          <div>
            <span class="ai-summary-item-text" v-html="parseMarkdown(item.markdown)"></span>
          </div>
          <a
            :href="item.url"
            class="coro-link text-decoration-underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ item.label }}
          </a>
        </li>
      </ol>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { EventsSummaryContent } from "@/_store/ai-assistant.module";
import { parseMarkdown } from "@/_helpers/markdown";

export default defineComponent({
  props: {
    content: {
      type: Object as PropType<EventsSummaryContent>,
      required: true,
    },
  },
  emits: ["learn-more"],
  setup(props, { emit }) {
    const onLearnMore = (item: string) => {
      emit("learn-more", item);
    };

    return {
      parseMarkdown,
      onLearnMore,
    };
  },
});
</script>

<style lang="scss" scoped>
.ai-summary-item-text {
  /* Allow long tokens (URLs, hashes) to wrap instead of forcing horizontal scroll. */
  overflow-wrap: break-word;
  /* Keep break-word for older engines that don't fully honor overflow-wrap. */
  word-break: break-word;

  :deep(p) {
    margin: 0;
  }

  :deep(strong) {
    font-weight: 600;
  }

  :deep(em) {
    font-style: italic;
  }

  :deep(code) {
    background-color: rgba(0, 0, 0, 0.1);
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }

  :deep(a) {
    font-family: "Source Sans Pro", sans-serif;
    font-size: 12px;
    color: rgb(var(--v-theme-link));
    text-decoration: underline;
    font-weight: 400;
  }

  :deep(ul),
  :deep(ol) {
    margin: 4px 0;
    padding-left: 16px;
  }

  :deep(li) {
    margin-bottom: 2px;
  }

  :deep(blockquote) {
    border-left: 3px solid rgb(var(--v-theme-gray-light));
    padding-left: 8px;
    margin: 4px 0;
    color: rgb(var(--v-theme-gray-medium));
    font-style: italic;
  }
}
</style>
